import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Button, Form, Table, Badge } from "react-bootstrap";
import { ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { FilterSelect, FilterDate, ClaimLink, firstOfLastMonth, today } from "../components/reports/reportShared.jsx";
import { getFeedbackReport, listEntityRows, listUsers } from "../services/apiClient.js";
import { useMasterCompanyNames } from "../services/useMasterCompanies.js";
import { formatDate } from "../utils/date.js";

// Adverse / Positive Feedback (legacy AdverseFeedbackReport.aspx /
// PositiveFeedback.aspx): progress entries flagged as complaints / positive
// feedback in a date window. 1.0 fixes: the end date now INCLUDES its whole
// day, and it defaults to today (1.0 had none). Positive's per-row Remove is
// ported working (1.0's never did anything — missing hidden field); it's a
// session-local dismiss for building a clean print list.

// Show Columns (Robert 2026-07-17 — different companies want different
// views): every column can be hidden; the choice is remembered per browser,
// separately for each report. Adverse swaps positive's Remove for Days To
// Resolve; its Feedback cell carries the resolution line when resolved.
const columnDefs = (adverse) => [
  ["date", "Date"], ["job", "Job #"], ["type", "Type"], ["pco", "PCO"],
  ["insurer", "Insurance Co"], ["assessor_co", "Assessor Co"], ["assessor", "Assessor"],
  ["claim", "Claim #"], ["client", "Client Name"], ["address", "Address"],
  ...(adverse ? [["days", "Days To Resolve"]] : []),
  ["feedback", "Feedback"],
  ...(adverse ? [] : [["remove", "Remove"]]),
];
const colsKey = (kind) => `feedbackCols:${kind}:v1`;

// Row highlighting (1.0's Highlight Assessor/Insurance Co — Robert 2026-07-17,
// "they will ask for it"): bootstrap row variants, no custom CSS; remembered
// per browser.
const highlightKey = (kind) => `feedbackHighlight:${kind}:v1`;
const HIGHLIGHT_COLOURS = [
  ["secondary", "Grey"], ["warning", "Yellow"], ["info", "Blue"],
  ["success", "Green"], ["danger", "Pink"],
];

function FeedbackReportPage({ kind }) {
  // Master companies are data now (Owner Data); same name keeps the diff and
  // the saved-search values identical to the old config constant.
  const COMPANY_GROUP_KEYS = useMasterCompanyNames();
  const adverse = kind === "adverse";
  const title = adverse ? "Adverse Feedback" : "Positive Feedback";
  const [filters, setFilters] = useState({
    from: firstOfLastMonth(), to: today(), insurer: "", assessor_company_id: "",
    assessor_id: "", project_coordinator_id: "", job_type_id: "", hide_tender: false,
  });
  const [lookups, setLookups] = useState({});
  const [report, setReport] = useState(null);
  const [removed, setRemoved] = useState(() => new Set()); // positive: dismissed rows
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const COLUMN_DEFS = columnDefs(adverse);
  const loadCols = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(colsKey(kind)));
      if (Array.isArray(saved)) return new Set(saved);
    } catch { /* fall through */ }
    return new Set(columnDefs(adverse).map(([k]) => k));
  };
  const [cols, setCols] = useState(loadCols);
  const col = (k) => cols.has(k);
  const loadHighlight = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(highlightKey(kind)));
      if (saved && typeof saved === "object") return saved;
    } catch { /* fall through */ }
    return { assessor_co: "", assessor_colour: "secondary", insurer: "", insurer_colour: "secondary" };
  };
  const [highlight, setHighlight] = useState(loadHighlight);
  const setHl = (k) => (v) => {
    setHighlight((h) => {
      const next = { ...h, [k]: v };
      localStorage.setItem(highlightKey(kind), JSON.stringify(next));
      return next;
    });
  };
  const rowVariant = (r) => {
    if (highlight.assessor_co && String(r.assessor_company_id) === String(highlight.assessor_co))
      return highlight.assessor_colour;
    if (highlight.insurer && String(r.insurer_company_id) === String(highlight.insurer))
      return highlight.insurer_colour;
    return undefined;
  };
  const toggleCol = (k) => {
    setCols((c) => {
      const next = new Set(c);
      if (next.has(k)) next.delete(k); else next.add(k);
      localStorage.setItem(colsKey(kind), JSON.stringify([...next]));
      return next;
    });
  };

  useEffect(() => {
    Promise.all(
      ["insurer-companies", "assessor-companies", "assessors", "job-types"].map((k) =>
        listEntityRows(k).then((rows) => [k, rows]).catch(() => [k, []])
      )
    ).then((pairs) => setLookups(Object.fromEntries(pairs)));
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
    setCols(loadCols());
    setHighlight(loadHighlight());
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  async function load(f = filters) {
    setError("");
    try {
      setReport(await getFeedbackReport(kind, { ...f, hide_tender: f.hide_tender ? "1" : "" }));
      setRemoved(new Set());
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load the report.");
    }
  }
  const set = (name) => (value) => {
    const next = { ...filters, [name]: value };
    setFilters(next);
    load(next);
  };

  const rows = (report?.rows || []).filter((r) => !removed.has(r.progress_id));

  // What this copy covers, for the masthead. Assessor company first: that is the
  // key GPS reports on (docs/business-rules/kpi-report.md §2), with the insurer as
  // the fallback and "All companies" when neither is filtered.
  const nameOf = (key, id) =>
    (id && (lookups[key] || []).find((r) => String(r.id) === String(id))?.name) || null;
  const scopeLabel =
    nameOf("assessor-companies", filters.assessor_company_id) ||
    (COMPANY_GROUP_KEYS.includes(filters.insurer)
      ? `${filters.insurer} (group)`
      : nameOf("insurer-companies", filters.insurer)) ||
    "All companies";

  return (
    <section className="page-panel">
      {/* App chrome: the document below carries its own GPS masthead, so this
          would only repeat the title on paper (see report-document.css). */}
      <div className="page-heading no-print"><h2>{title} <RulesHelp topic="feedback" /></h2></div>

      <div className="report-controls d-flex align-items-end column-gap-2 row-gap-2 flex-wrap mb-3 no-print">
        <FilterDate label="From" value={filters.from} onChange={set("from")} />
        <FilterDate label="To" value={filters.to} onChange={set("to")} />
        <FilterSelect label="Insurer" value={filters.insurer} onChange={set("insurer")} options={lookups["insurer-companies"]}>
          {COMPANY_GROUP_KEYS.map((g) => <option key={g} value={g}>{g} (group)</option>)}
        </FilterSelect>
        <FilterSelect label="Assessor Company" value={filters.assessor_company_id} onChange={set("assessor_company_id")} options={lookups["assessor-companies"]} />
        <FilterSelect label="Assessor" value={filters.assessor_id} onChange={set("assessor_id")} options={lookups.assessors} />
        <FilterSelect label="Job Type" value={filters.job_type_id} onChange={set("job_type_id")} options={lookups["job-types"]} />
        <FilterSelect label="Project Co-Ordinator" value={filters.project_coordinator_id} onChange={set("project_coordinator_id")} options={users} labelField="full_name" width={180} />
        <Form.Check
          type="switch" id="hide-tender" label="Hide tender jobs" className="mb-1"
          checked={!!filters.hide_tender}
          onChange={(e) => set("hide_tender")(e.target.checked)}
        />
        {!adverse && removed.size > 0 ? (
          <Button size="sm" variant="outline-secondary" onClick={() => setRemoved(new Set())}>
            Reset removed ({removed.size})
          </Button>
        ) : null}
      </div>

      <div className="d-flex align-items-center column-gap-3 flex-wrap mb-2 no-print">
          <span className="small fw-semibold text-muted">Show columns:</span>
          {COLUMN_DEFS.map(([k, label]) => (
            <Form.Check
              key={k} inline type="checkbox" id={`col-${k}`} label={label}
              className="small" checked={cols.has(k)} onChange={() => toggleCol(k)}
            />
          ))}
      </div>

      <div className="d-flex align-items-end column-gap-2 row-gap-2 flex-wrap mb-2 no-print">
          <span className="small fw-semibold text-muted mb-1">Highlight:</span>
          <FilterSelect label="Assessor Co" value={highlight.assessor_co} onChange={setHl("assessor_co")} options={lookups["assessor-companies"]} width={180} />
          <Form.Group>
            <Form.Label className="mb-0 small fw-semibold">Colour</Form.Label>
            <ThemedSelect size="sm" style={{ width: 100 }} value={highlight.assessor_colour} onChange={(e) => setHl("assessor_colour")(e.target.value)}>
              {HIGHLIGHT_COLOURS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </ThemedSelect>
          </Form.Group>
          <FilterSelect label="Insurance Co" value={highlight.insurer} onChange={setHl("insurer")} options={lookups["insurer-companies"]} width={180} />
          <Form.Group>
            <Form.Label className="mb-0 small fw-semibold">Colour</Form.Label>
            <ThemedSelect size="sm" style={{ width: 100 }} value={highlight.insurer_colour} onChange={(e) => setHl("insurer_colour")(e.target.value)}>
              {HIGHLIGHT_COLOURS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </ThemedSelect>
          </Form.Group>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <div className="report-doc report-doc-wide border rounded">
          {/* Same masthead as the KPI pack: these two go to the same insurer, often
              in the same email, so they should look like one company's work. */}
          <ReportMasthead
            report={title}
            title={scopeLabel}
            subtitle={`Grayburn Property Services · ${formatDate(filters.from)} to ${formatDate(filters.to)}`}
          />
          <div className="report-body">
          <Table striped bordered hover responsive size="sm" className="align-middle report-table">
            <thead className="table-surface">
              <tr>
                {col("date") ? <th className="text-nowrap">Date</th> : null}
                {col("job") ? <th>Job #</th> : null}
                {col("type") ? <th>Type</th> : null}
                {col("pco") ? <th>PCO</th> : null}
                {col("insurer") ? <th>Insurer</th> : null}
                {col("assessor_co") ? <th>Assessor Co</th> : null}
                {col("assessor") ? <th>Assessor</th> : null}
                {col("claim") ? <th>Claim #</th> : null}
                {col("client") ? <th>Client</th> : null}
                {col("address") ? <th>Address</th> : null}
                {adverse && col("days") ? <th className="text-nowrap">Days To Resolve</th> : null}
                {col("feedback") ? <th className="report-col-main" style={{ minWidth: 280 }}>Feedback</th> : null}
                {!adverse && col("remove") ? <th className="no-print" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.progress_id} className={rowVariant(r) ? `table-${rowVariant(r)}` : undefined}>
                  {col("date") ? <td className="text-nowrap">{formatDate(r.date_entered)}</td> : null}
                  {col("job") ? <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} tab="progress" /></td> : null}
                  {col("type") ? <td className="text-nowrap">{r.job_type_name || "-"}</td> : null}
                  {col("pco") ? <td className="text-nowrap">{r.project_coordinator_name || "-"}</td> : null}
                  {col("insurer") ? <td className="small">{r.insurer_name || "-"}</td> : null}
                  {col("assessor_co") ? <td className="small">{r.assessor_company_name || "-"}</td> : null}
                  {col("assessor") ? <td className="small">{r.assessor_name || "-"}</td> : null}
                  {col("claim") ? <td className="text-nowrap">{r.insurance_claim_number || "-"}</td> : null}
                  {col("client") ? <td className="small">{r.client_name || "-"}</td> : null}
                  {col("address") ? <td className="small">{r.street_address || "-"}</td> : null}
                  {adverse && col("days") ? (
                    <td className="text-nowrap">
                      {r.days_to_resolve == null ? (
                        <Badge bg="danger">Not Resolved</Badge>
                      ) : (
                        `${r.days_to_resolve} day${r.days_to_resolve === 1 ? "" : "s"}`
                      )}
                    </td>
                  ) : null}
                  {col("feedback") ? <td className="report-col-main" style={{ whiteSpace: "pre-wrap" }}>
                    {r.feedback}
                    {adverse && r.complaint_resolution_date ? (
                      <div className="small text-muted mt-1">
                        <strong>Resolved {formatDate(r.complaint_resolution_date)}:</strong>{" "}
                        {r.complaint_resolution_details || ""}
                      </div>
                    ) : null}
                  </td> : null}
                  {!adverse && col("remove") ? (
                    <td className="text-end no-print">
                      <Button
                        size="sm" variant="outline-secondary" data-hint="Remove from this list (until reset)"
                        onClick={() => setRemoved((s) => new Set(s).add(r.progress_id))}
                      >
                        ×
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr><td colSpan={adverse ? 10 : 10} className="text-center text-muted">No entries.</td></tr>
              ) : null}
            </tbody>
          </Table>
          <div className="small text-muted">
            {report.rows.length >= report.cap
              ? `First ${report.cap} shown — narrow the dates.`
              : `${rows.length} entr${rows.length === 1 ? "y" : "ies"} found.`}
          </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default FeedbackReportPage;
